import React, { RefObject } from 'react'
import { Box, Text } from '@workwhile/ui'
import { User } from 'api/typings'
import { UserOutstandingBalance } from 'api/payments'
import { AmountEntry } from './AmountEntry'

interface UserBoxProps {
  user: User
  outstandingBalance: UserOutstandingBalance
  userBoxRef: RefObject<HTMLDivElement | null>
}

export const UserBox = ({
  user,
  outstandingBalance,
  userBoxRef,
}: UserBoxProps) => {
  const stripeEarnings = outstandingBalance.earningsWithStripe
  const highnoteEarnings = outstandingBalance.earningsWithHighnote ?? 0
  const marqetaEarnings = outstandingBalance.earningsWithMarqeta
  const evereeEarnings =
    outstandingBalance.earningsWithEveree +
    outstandingBalance.pendingEvereePayouts
  const bbsiEarnings = outstandingBalance.completedBbsiPayouts

  const showStripe = stripeEarnings !== 0
  const showHighnote = highnoteEarnings !== 0
  const showMarqeta = marqetaEarnings !== 0
  const showEveree = evereeEarnings !== 0
  const showBbsi = bbsiEarnings > 0

  const show1099 = showStripe || showHighnote || showMarqeta
  const showW2 = showEveree || showBbsi
  const total =
    stripeEarnings +
    highnoteEarnings +
    marqetaEarnings +
    evereeEarnings +
    bbsiEarnings

  return (
    <Box
      ref={userBoxRef}
      position="relative"
      bg="white"
      border="1px solid"
      borderColor="border"
      borderRadius="medium"
      p={4}
      minWidth="250px"
      maxWidth="280px"
      textAlign="center"
      boxShadow="small"
      zIndex={3}
      flexShrink={0}
    >
      <Text fontWeight="bold" color="primary" fontSize={5} mb={2}>
        {user.name}{' '}
        <Text color="primary" inline>
          ({user.id})
        </Text>
      </Text>
      <Text fontWeight="bold" fontSize={3} mb={3} textAlign="left">
        Lifetime Earnings
      </Text>
      {show1099 ? (
        <>
          <Text fontWeight="bold" fontSize={2} textAlign="left">
            1099
          </Text>
          {showStripe ? (
            <AmountEntry
              ml={2}
              labelProps={{ color: 'lightText' }}
              label="Stripe"
              value={stripeEarnings}
            />
          ) : null}
          {showHighnote ? (
            <AmountEntry
              ml={2}
              labelProps={{ color: 'lightText' }}
              label="Highnote"
              value={highnoteEarnings}
            />
          ) : null}
          {showMarqeta ? (
            <AmountEntry
              ml={2}
              labelProps={{ color: 'lightText' }}
              label="Marqeta"
              value={marqetaEarnings}
            />
          ) : null}
        </>
      ) : null}
      {show1099 && showW2 ? <hr /> : null}
      {showW2 ? (
        <>
          <Text fontWeight="bold" fontSize={2} textAlign="left">
            W2
          </Text>
          {showEveree ? (
            <AmountEntry
              ml={2}
              labelProps={{ color: 'lightText' }}
              label="Everee"
              value={evereeEarnings}
            />
          ) : null}
          {showBbsi ? (
            <AmountEntry
              ml={2}
              labelProps={{ color: 'lightText' }}
              label="BBSI"
              value={bbsiEarnings}
            />
          ) : null}
        </>
      ) : null}
      {show1099 || showW2 ? <hr /> : null}
      <AmountEntry
        mb={1}
        labelProps={{ fontWeight: 'bold' }}
        label="Total"
        value={total}
      />
    </Box>
  )
}
